import { Vignette, VignetteResponse } from '../types/Vignette';
import { getVignetteVariantView } from './vignetteVariants';

// A student can meet the same variant again in a later block, and each block
// then keeps its own response row (ORA-465). Passing `blockId` asks for the
// answer given IN THAT BLOCK, so a repeat starts unanswered instead of
// inheriting the earlier block's answer and verdict. Rows that belong to no
// block (chat-answered, legacy) belong to no block's study surface either.
// Omit `blockId` for the surfaces that read the whole history.
export const getVignetteResponses = (
  vignette?: Vignette | null,
  blockId?: string | null
): VignetteResponse[] => {
  if (!vignette) return [];

  const responses = vignette.responses?.length
    ? vignette.responses
    : vignette.response
      ? [vignette.response]
      : [];

  const scopedResponses = blockId
    ? responses.filter((response) => response.blockId === blockId)
    : responses;

  return [...scopedResponses].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
};

export const getLatestVignetteResponse = (
  vignette?: Vignette | null,
  blockId?: string | null
): VignetteResponse | undefined => {
  const responses = getVignetteResponses(vignette, blockId);
  return responses[responses.length - 1];
};

// A vignette whose every variant view is review-only: a "View All" opened
// while nothing is answerable. Block scoping exists to keep a repeat blank
// until it is answered again; here nothing can be answered, so the earlier
// graded answer the server attached to the view IS the content to show.
export const isReadOnlyVignetteView = (vignette?: Vignette | null): boolean =>
  Boolean(vignette?.variantViews?.length) &&
  (vignette?.variantViews ?? []).every(
    (variantView) => variantView.isReviewOnly && !variantView.isAnswerable
  );

export const getScoringVignetteResponse = (
  vignette?: Vignette | null,
  blockId?: string | null
): VignetteResponse | undefined => {
  const responses = getVignetteResponses(vignette, blockId);

  const scoped =
    responses.find((response) => !response.isRetry) ?? responses[0];

  if (scoped || !isReadOnlyVignetteView(vignette)) return scoped;

  return getVignetteVariantView(vignette)?.response ?? undefined;
};

export const getAttemptedAnswers = (vignette?: Vignette | null): string[] => {
  return getVignetteResponses(vignette)
    .filter((response) => response.isSubmitted)
    .map((response) => response.answer)
    .filter(Boolean);
};

const normalizeAnswerLetter = (value?: string | null) =>
  (value ?? '').trim().toUpperCase();

// Whether a single response scored as correct. Trusts the backend-scored
// `correct` flag, but also falls back to comparing the stored answer against
// the stored correct answer so the optimistic client response (whose `correct`
// may be computed from a raw, un-normalized letter) is still recognised.
const isResponseScoredCorrect = (response: VignetteResponse): boolean => {
  if (response.correct === true) return true;
  const answer = normalizeAnswerLetter(response.answer);
  return (
    answer.length > 0 &&
    answer === normalizeAnswerLetter(response.correctAnswer)
  );
};

// Whether the user has answered this vignette correctly for scheduling
// purposes. Mirrors the backend's `hasCorrectAnswer` predicate in
// `recomputeVignetteSchedulingFromResponses` (ora-be): a submitted, non-retry
// response that is correct and not a lucky guess. Once this is true the backend
// clears the vignette's scheduling (dropping `dueAt`/`nextVariantPreview`), so
// the FE must treat the scheduled-variant badge as gone the moment the
// (optimistic) correct response lands — otherwise the badge briefly flashes in
// before the server response arrives and clears the stale scheduling.
export const hasCorrectScoringResponse = (
  vignette?: Vignette | null
): boolean =>
  getVignetteResponses(vignette).some(
    (response) =>
      response.isSubmitted &&
      !response.isRetry &&
      response.luckyGuess !== true &&
      isResponseScoredCorrect(response)
  );

export const getDisplayedVignetteResponse = (
  vignette?: Vignette | null,
  viewedVariantId?: string | null,
  blockId?: string | null
): VignetteResponse | undefined => {
  const variantView = getVignetteVariantView(vignette, viewedVariantId);
  if (
    variantView?.response &&
    (!blockId ||
      variantView.response.blockId === blockId ||
      isReadOnlyVignetteView(vignette))
  ) {
    return variantView.response;
  }

  return getLatestVignetteResponse(vignette, blockId);
};
